import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { isSearchEnhancementsMilestone1Enabled } from 'feature-flags';
import type { AnalyticsMetaData } from 'state/actions/search';
import { Container } from 'view/search-modal/container';
import { ResetSearchOnUnMount } from 'view/search-modal/reset-search-on-unmount';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { BackButton } from '@atlassian/help-center-common-component/back-button';
import { Flipped } from '@atlassian/help-center-common-component/flip-toolkit';
import messages from './messages';
import {
    Button,
    Root,
    Input,
    HiddenLabel,
    InputBackground,
    Form,
    BackButtonContainer,
    SearchTextField,
    Icon,
} from './styled';

export interface SearchBarProps {
    onRequestClose: () => void;
    onChange: (term: string, meta?: AnalyticsMetaData) => void;
    resetSearch?: () => void;
    term: string;
    className?: string;
}

export interface Props extends SearchBarProps {
    onSubmit: () => void;
    onFocus: () => void;
}

const highlightInputText = (ref: HTMLInputElement | null) => {
    if (ref) {
        // We want to delay the focus to the next tick so react-best-modal picks up the
        // previously focused element correctly.
        setTimeout(() => ref.select(), 0);
    }
};

const INPUT_ID = 'px-search-query';

interface SearchFieldProps {
    term: string;
    placeholder: string;
    elemBeforeInput: JSX.Element;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus: () => void;
}

const SearchField = (props: SearchFieldProps) => {
    const { term, placeholder, elemBeforeInput, onChange, onFocus } = props;

    return (
        <>
            {isSearchEnhancementsMilestone1Enabled() ? (
                <SearchTextField
                    id={INPUT_ID}
                    value={term}
                    onChange={onChange}
                    type="text"
                    name="term"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={placeholder}
                    required
                    onFocus={onFocus}
                    // Disabling existing violations, should be fixed when revisited.
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    elemBeforeInput={elemBeforeInput}
                />
            ) : (
                <Input
                    id={INPUT_ID}
                    value={term}
                    onChange={onChange}
                    type="text"
                    name="term"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={placeholder}
                    required
                    innerRef={highlightInputText}
                    onFocus={onFocus}
                    // Disabling existing violations, should be fixed when revisited.
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                />
            )}
        </>
    );
};

export class SearchBar extends React.PureComponent<Props & InjectedIntlProps> {
    onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.onChange(e.target.value);
    };

    onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        this.props.onSubmit();
    };

    render() {
        const { onFocus, onRequestClose, className, term, intl } = this.props;

        return (
            <ResetSearchOnUnMount resetSearch={this.props.resetSearch}>
                <Root className={className}>
                    <HiddenLabel htmlFor={INPUT_ID}>{intl.formatMessage(messages.input)}</HiddenLabel>

                    <Container>
                        <Flipped flipId="background" translate scale>
                            <InputBackground term={term} />
                        </Flipped>
                        <Flipped flipId="icon" translate>
                            {isSearchEnhancementsMilestone1Enabled() ? (
                                <BackButtonContainer>
                                    <BackButton onClick={onRequestClose} actionSubjectId="closeSearch" />
                                </BackButtonContainer>
                            ) : (
                                <Button
                                    onClick={onRequestClose}
                                    actionSubjectId="closeSearch"
                                    data-test-id="back-button"
                                >
                                    <CrossIcon
                                        primaryColor={token('color.icon', colors.N500)}
                                        label={intl.formatMessage(messages.close)}
                                    />
                                </Button>
                            )}
                        </Flipped>

                        {/* This Flipped element has to wrap the form element or there will be strange behaviour in safari */}
                        <Flipped flipId="search" translate>
                            <Form onSubmit={this.onSubmit}>
                                <SearchField
                                    term={term}
                                    onChange={this.onChange}
                                    placeholder={intl.formatMessage(messages.input)}
                                    onFocus={onFocus}
                                    elemBeforeInput={
                                        <Icon>
                                            <SearchIcon
                                                label="search icon"
                                                aria-hidden="true"
                                                size="small"
                                                primaryColor={token('color.icon', colors.N500)}
                                            />
                                        </Icon>
                                    }
                                />
                            </Form>
                        </Flipped>
                    </Container>
                </Root>
            </ResetSearchOnUnMount>
        );
    }
}

export default injectIntl(SearchBar);
